import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Drawer,
  EmptyState,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
} from "../../design-system";
import {
  acknowledgeInternalDocument,
  archiveInternalDocument,
  deleteInternalDocument,
  downloadInternalDocument,
  getDocumentationLevels,
  getInternalDocument,
  getInternalDocumentAcknowledgements,
  getInternalDocumentTypes,
  getInternalDocuments,
  publishInternalDocument,
  unarchiveInternalDocument,
} from "../../helpers/internal_documentation_helper";
import { getEmployees } from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { unwrap } from "./helpers";
import { useInternalDocumentPreview } from "./useInternalDocumentPreview";
import { DocumentsTable } from "./components/DocumentsTable";
import { DocumentsFilterBar } from "./components/DocumentsFilterBar";
import { DocumentDetail } from "./components/DocumentDetail";
import { UploadDocumentDrawer } from "./components/UploadDocumentDrawer";
import { UploadNewVersionDrawer } from "./components/UploadNewVersionDrawer";

function DocumentacionInterna() {
  document.title = "Documentación interna | Kronaly";
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useCurrentMembership();
  // En este módulo, "ser gestor" significa poder publicar (subir / archivar
  // / borrar). Lo mapeamos a la capability fina; un employee con override
  // `internal_docs:publish=true` actúa como manager aquí.
  const isManager = can("internal_docs:publish");
  const canManageCatalogs = can("internal_docs:manage_catalogs");

  const [docs, setDocs] = useState([]);
  const [levels, setLevels] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [openDocId, setOpenDocId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [openDocLoading, setOpenDocLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showNewVersion, setShowNewVersion] = useState(false);

  // Acuses de lectura (manager view del doc abierto)
  const [ackList, setAckList] = useState(null);
  const [ackListLoading, setAckListLoading] = useState(false);
  const [ackListError, setAckListError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const { previewUrl, previewLoading, previewError } = useInternalDocumentPreview(openDoc);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Manager pide include_archived para ver todo; resto deja que el backend
      // filtre por su scope (published + propios drafts si es autor).
      const params = isManager ? { include_archived: 1 } : {};
      const [docsRes, levelsRes, typesRes, empRes] = await Promise.all([
        getInternalDocuments(params),
        getDocumentationLevels().catch(() => ({ data: [] })),
        getInternalDocumentTypes().catch(() => ({ data: [] })),
        isManager ? getEmployees().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setDocs(unwrap(docsRes) || []);
      setLevels(unwrap(levelsRes) || []);
      setTypes(unwrap(typesRes) || []);
      setEmployees(unwrap(empRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar la documentación.");
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    load();
  }, [load]);

  // Carga el detalle completo del doc abierto (con version_history y
  // my_acknowledgement actualizado). Refresh manual cambiando reloadKey.
  const [openDocReloadKey, setOpenDocReloadKey] = useState(0);
  useEffect(() => {
    if (!openDocId) {
      setOpenDoc(null);
      setOpenDocLoading(false);
      return;
    }
    let cancelled = false;
    setOpenDocLoading(true);
    getInternalDocument(openDocId)
      .then((res) => {
        if (cancelled) return;
        setOpenDoc(unwrap(res) || res?.data || res || null);
      })
      .catch(() => {
        if (cancelled) return;
        setOpenDoc(null);
        toast.error("No se pudo cargar el documento.");
      })
      .finally(() => {
        if (cancelled) return;
        setOpenDocLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openDocId, openDocReloadKey, toast]);

  // Carga el listado de acuses cuando un manager abre un doc con lectura obligatoria.
  const openDocRequiresAck = openDoc?.requires_acknowledgement === true;
  useEffect(() => {
    if (!openDocId || !isManager || !openDocRequiresAck) {
      setAckList(null);
      setAckListError(null);
      setAckListLoading(false);
      return;
    }
    let cancelled = false;
    setAckListLoading(true);
    setAckListError(null);
    getInternalDocumentAcknowledgements(openDocId)
      .then((res) => {
        if (cancelled) return;
        setAckList(unwrap(res) || res?.data || res || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setAckListError(
          typeof e === "string" ? e : e?.message || "No se pudieron cargar los acuses.",
        );
      })
      .finally(() => {
        if (cancelled) return;
        setAckListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openDocId, openDocRequiresAck, isManager]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs
      .filter((d) => (levelFilter === "all" ? true : d.level_id === levelFilter))
      .filter((d) => (typeFilter === "all" ? true : d.type_id === typeFilter))
      .filter((d) => (isManager && statusFilter !== "all" ? d.status === statusFilter : true))
      .filter((d) => {
        if (!q) return true;
        const haystack = [d.code, d.title, d.original_filename, d.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (b.published_at || b.id).localeCompare(a.published_at || a.id));
  }, [docs, search, levelFilter, typeFilter, statusFilter, isManager]);

  // Posibles padres en el upload: solo publicados (no encadenar drafts).
  const possibleParents = useMemo(
    () => docs.filter((d) => d.status === "published"),
    [docs],
  );

  const onDownload = async (doc) => {
    try {
      await downloadInternalDocument(doc.id, doc.original_filename || doc.title);
    } catch (e) {
      toast.error("No se pudo descargar", typeof e === "string" ? e : e?.message);
    }
  };

  const onPublish = async (doc) => {
    try {
      await publishInternalDocument(doc.id);
      toast.success("Documento publicado");
      setOpenDocId(null);
      load();
    } catch (e) {
      toast.error("No se pudo publicar", typeof e === "string" ? e : e?.message);
    }
  };

  const onArchive = async (doc) => {
    const ok = await confirm({
      title: "Archivar documento",
      description: `Archivar "${doc.title}" (${doc.code}). Dejará de aparecer en los listados activos.`,
      confirmLabel: "Archivar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await archiveInternalDocument(doc.id);
      toast.success("Documento archivado");
      setOpenDocId(null);
      load();
    } catch (e) {
      toast.error("No se pudo archivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onUnarchive = async (doc) => {
    try {
      await unarchiveInternalDocument(doc.id);
      toast.success("Documento desarchivado");
      setOpenDocId(null);
      load();
    } catch (e) {
      toast.error("No se pudo desarchivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onAcknowledge = async () => {
    if (!openDoc) return;
    setAcknowledging(true);
    try {
      await acknowledgeInternalDocument(openDoc.id);
      const acknowledgedAt = new Date().toISOString();
      // Actualiza optimísticamente el doc abierto y el listado.
      setOpenDoc((prev) =>
        prev
          ? { ...prev, my_acknowledgement: { status: "done", acknowledged_at: acknowledgedAt } }
          : prev,
      );
      setDocs((prev) =>
        prev.map((d) =>
          d.id === openDoc.id
            ? { ...d, my_acknowledgement: { status: "done", acknowledged_at: acknowledgedAt } }
            : d,
        ),
      );
      toast.success("Marcado como leído");
    } catch (e) {
      toast.error("No se pudo registrar el acuse", typeof e === "string" ? e : e?.message);
    } finally {
      setAcknowledging(false);
    }
  };

  const onDelete = async (doc) => {
    const ok = await confirm({
      title: "Eliminar documento",
      description: `Eliminar definitivamente "${doc.title}" (${doc.code}). El archivo se borra del almacenamiento. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteInternalDocument(doc.id);
      toast.success("Documento eliminado");
      setOpenDocId(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Documentación"
        title="Documentación interna"
        description="Políticas, procedimientos e instrucciones técnicas del sistema documental."
        actions={
          <>
            {canManageCatalogs && (
              <Link to="/documentacion-interna/configuracion" style={{ textDecoration: "none" }}>
                <Button variant="ghost" icon="Settings">
                  Niveles y tipos
                </Button>
              </Link>
            )}
            {isManager && (
              <Button variant="primary" icon="Upload" onClick={() => setShowUpload(true)}>
                Subir documento
              </Button>
            )}
          </>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {!loading && isManager && levels.length === 0 && (
        <Alert tone="warning">
          {canManageCatalogs ? (
            <>
              Aún no hay niveles configurados. Crea al menos uno desde{" "}
              <Link to="/documentacion-interna/configuracion">Niveles y tipos</Link> antes de subir
              documentos.
            </>
          ) : (
            <>Aún no hay niveles configurados. Pide a un administrador que los cree antes de subir documentos.</>
          )}
        </Alert>
      )}

      <Card padded={false}>
        <DocumentsFilterBar
          search={search}
          onSearchChange={setSearch}
          levels={levels}
          types={types}
          levelFilter={levelFilter}
          onLevelFilterChange={setLevelFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          isManager={isManager}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          count={filtered.length}
        />

        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="FileText"
            title={search.trim() || levelFilter !== "all" || typeFilter !== "all" ? "Sin resultados" : "Sin documentos todavía"}
            description={
              search.trim() || levelFilter !== "all" || typeFilter !== "all"
                ? "Prueba con otros filtros o búsqueda."
                : isManager
                  ? "Sube el primer documento de la jerarquía."
                  : "Cuando RRHH publique documentos aparecerán aquí."
            }
          />
        ) : (
          <DocumentsTable
            rows={filtered}
            isManager={isManager}
            onOpen={(doc) => setOpenDocId(doc.id)}
            onDownload={onDownload}
          />
        )}
      </Card>

      <Drawer
        open={!!openDocId}
        onClose={() => setOpenDocId(null)}
        size="lg"
        title={openDoc?.title || (openDocLoading ? "Cargando…" : "Documento")}
        footer={
          openDoc ? (
            <>
              {isManager && openDoc.status === "draft" && (
                <Button variant="primary" icon="Check" onClick={() => onPublish(openDoc)}>
                  Publicar
                </Button>
              )}
              {isManager && openDoc.status === "published" && openDoc.is_current && (
                <Button variant="secondary" icon="Upload" onClick={() => setShowNewVersion(true)}>
                  Subir nueva versión
                </Button>
              )}
              {isManager && openDoc.status === "published" && (
                <Button variant="secondary" icon="Archive" onClick={() => onArchive(openDoc)}>
                  Archivar
                </Button>
              )}
              {isManager && openDoc.status === "archived" && (
                <Button variant="secondary" icon="ArchiveRestore" onClick={() => onUnarchive(openDoc)}>
                  Desarchivar
                </Button>
              )}
              {isManager && (
                <Button variant="dangerSoft" icon="Trash2" onClick={() => onDelete(openDoc)}>
                  Eliminar
                </Button>
              )}
              <div style={{ flex: 1 }} />
              <Button variant="ghost" icon="Download" onClick={() => onDownload(openDoc)}>
                Descargar
              </Button>
            </>
          ) : null
        }
      >
        {openDocLoading && !openDoc ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : openDoc ? (
          <DocumentDetail
            doc={openDoc}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
            onAcknowledge={onAcknowledge}
            acknowledging={acknowledging}
            ackList={ackList}
            ackListLoading={ackListLoading}
            ackListError={ackListError}
            isManager={isManager}
            onOpenVersion={(id) => setOpenDocId(id)}
          />
        ) : null}
      </Drawer>

      {isManager && (
        <UploadDocumentDrawer
          open={showUpload}
          onClose={() => setShowUpload(false)}
          levels={levels}
          types={types}
          parents={possibleParents}
          employees={employees}
          onUploaded={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}

      {isManager && (
        <UploadNewVersionDrawer
          open={showNewVersion}
          onClose={() => setShowNewVersion(false)}
          currentDoc={openDoc}
          onUploaded={() => {
            setShowNewVersion(false);
            // Refresca el listado y el detalle (para que aparezca el draft v_n+1
            // en el historial). El doc abierto sigue siendo la versión vigente.
            load();
            setOpenDocReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

export default DocumentacionInterna;
